import { z } from 'zod';
import { command, get, post, put } from '../http';
import { type ListQuery, paginated, resource } from '../schemas/common';
import {
  adminDappRequestSchema,
  adminDashboardSchema,
  cronLogSchema,
  kycRequestSchema,
  newsItemSchema,
  settingSchema,
  userSchema,
  virtualPowerGrantSchema,
  walletAdjustmentSchema,
} from '../schemas/domain';

export const walletAdjustInput = z.object({
  user_code: z.string().min(3, 'Enter the member User ID'),
  wallet: z.enum(['activation', 'income']),
  amount_usd: z.coerce.number().positive('Amount must be greater than zero'),
  reason: z.string().min(4, 'A reason is required for the audit log'),
});
export type WalletAdjustInput = z.infer<typeof walletAdjustInput>;

export const newsInput = z.object({
  title: z.string().min(3),
  body: z.string().min(3),
  is_published: z.boolean().default(true),
});
export type NewsInput = z.infer<typeof newsInput>;

/**
 * Passwords/PINs are one-way hashed — there is no "current value" to show, only
 * a new one to set. Both admin-reset forms below collect a confirmation field
 * purely for client-side typo protection; only the primary field is sent.
 */
export const adminChangePasswordInput = z
  .object({
    password: z.string().min(8, 'Minimum 8 characters'),
    password_confirmation: z.string(),
  })
  .refine((v) => v.password === v.password_confirmation, {
    message: 'Passwords do not match',
    path: ['password_confirmation'],
  });
export type AdminChangePasswordInput = z.infer<typeof adminChangePasswordInput>;

export const adminResetPinInput = z
  .object({
    transaction_pin: z.string().regex(/^\d{4,8}$/, 'PIN must be 4-8 digits'),
    transaction_pin_confirmation: z.string(),
  })
  .refine((v) => v.transaction_pin === v.transaction_pin_confirmation, {
    message: 'PINs do not match',
    path: ['transaction_pin_confirmation'],
  });
export type AdminResetPinInput = z.infer<typeof adminResetPinInput>;

/** Grant virtual left/right leg units to a member's binary position. */
export const virtualPowerGrantInput = z.object({
  user_code: z.string().min(3, 'Enter the member User ID'),
  position: z.enum(['left', 'right']),
  units: z.coerce.number().int().positive('Units must be a positive whole number'),
  note: z.string().max(255).optional(),
});
export type VirtualPowerGrantInput = z.infer<typeof virtualPowerGrantInput>;

export const adminApi = {
  dashboard: () => get('/admin/dashboard', resource(adminDashboardSchema)),

  /* members */
  toggleStatus: (id: string) => command(`/admin/members/${id}/toggle`),
  block: (id: string, reason: string) => command(`/admin/members/${id}/block`, { reason }),
  /** Grants the $100 joining package (package selection is server-side). */
  activateMember: (id: string) => command(`/admin/members/${id}/activate`),
  changeMemberPassword: (id: string, input: AdminChangePasswordInput) =>
    command(`/admin/members/${id}/password`, { password: input.password }),
  resetMemberPin: (id: string, input: AdminResetPinInput) =>
    command(`/admin/members/${id}/pin`, { transaction_pin: input.transaction_pin }),

  /**
   * Assume a member's session. The user app renders the impersonation banner
   * (§6.2) whenever the resulting session reports is_impersonating.
   */
  impersonate: (id: string) => post(`/admin/members/${id}/impersonate`, resource(userSchema)),
  stopImpersonating: () => command('/admin/impersonate/stop'),

  /* wallet adjustments */
  adjustWallet: (mode: 'add' | 'deduct', input: WalletAdjustInput) =>
    command(`/admin/wallet/${mode}`, input),
  /** Past admin-initiated add/deduct adjustments, newest first — shown on both wallet/add and wallet/deduct. */
  walletHistory: (query: ListQuery = {}) =>
    get('/admin/wallet/history', paginated(walletAdjustmentSchema), query),

  /* virtual power */
  grantVirtualPower: (input: VirtualPowerGrantInput) =>
    post('/admin/virtual-power', resource(virtualPowerGrantSchema), input),
  /** Past grants, newest first. */
  virtualPowerHistory: (query: ListQuery = {}) =>
    get('/admin/virtual-power', paginated(virtualPowerGrantSchema), query),

  /* KYC */
  kycRequests: (query: ListQuery = {}) => get('/admin/kyc', paginated(kycRequestSchema), query),
  approveKyc: (id: string) => command(`/admin/kyc/${id}/approve`),
  rejectKyc: (id: string, note: string) => command(`/admin/kyc/${id}/reject`, { note }),

  /* system */
  news: () => get('/admin/news', resource(z.array(newsItemSchema))),
  /** Accepts a plain object, or a FormData when posting an image alongside the text fields. */
  addNews: (input: NewsInput | FormData) => post('/admin/news', resource(newsItemSchema), input),
  deleteNews: (id: string) => command(`/admin/news/${id}/delete`),

  settings: () => get('/admin/settings', resource(z.array(settingSchema))),
  updateSettings: (values: Record<string, string>) =>
    put('/admin/settings', resource(z.array(settingSchema)), { settings: values }),
  uploadSettingsQr: (form: FormData) => post('/admin/settings/qr', resource(settingSchema), form),
  sendTestEmail: (to: string) => command('/admin/settings/test-email', { to }),

  cronLog: (query: ListQuery = {}) => get('/admin/cron-log', paginated(cronLogSchema), query),
  clearCronLog: () => command('/admin/cron-log/clear'),

  /**
   * DApp (wallet) registration requests — client requirement: "SHOW ALL
   * NECESSARY REGISTRATION DAPP REQUESTS IN ADMIN AS WELL AS USER PANEL".
   */
  dappRequests: (status: 'pending' | 'confirmed' | 'rejected' | 'all', query: ListQuery = {}) =>
    get(`/admin/dapp-requests/${status}`, paginated(adminDappRequestSchema), query),
  recheckDappRequest: (id: string) =>
    post(`/admin/dapp-requests/${id}/recheck`, resource(adminDappRequestSchema)),
  rejectDappRequest: (id: string, note: string) =>
    post(`/admin/dapp-requests/${id}/reject`, resource(adminDappRequestSchema), { note }),
};
